import { api } from '../api/endpoints.js';
import { GameEngine } from '../game/engine.js';
import { render, setupCanvas } from '../game/render.js';
import { bindInput } from '../game/input.js';
import { floatScore, comboPopup, stagePopup, feverBanner, particleBurst, shake } from '../fx/effects.js';
import { showRespawnModal } from '../ui/modal-respawn.js';
import { openTopupSheet } from '../ui/bottom-sheet-topup.js';
import { backButton, mainButton, haptic } from '../tg.js';
import { tuning } from '../game/tuning.js';

// Game scene state machine: 'loading' | 'playing' | 'pending_respawn' | 'ending'
//
// Context expected from caller (set in menu/packs scene):
//   ctx.state.pack             — pack DTO ({id, name, cover_image_url, total_slots, milestones?})
//   ctx.state.activeSession    — session DTO from /current OR /sessions
//   ctx.state.startWithRespawnModal — boolean (true when resuming pending_respawn)
//
// On scene exit, all 3 fields cleaned.
export const gameScene = {
  async mount(root, ctx) {
    const pack = ctx.state.pack;
    const session = ctx.state.activeSession;
    if (!pack || !session) {
      console.warn('game scene: missing pack/session in state, returning to menu');
      return ctx.goto('menu');
    }

    // 1. Load milestones — may not be in pack (resume case from /current strips them)
    let milestones = pack.milestones;
    if (!Array.isArray(milestones) || milestones.length === 0) {
      try {
        const detail = await api.getPack(pack.id);
        milestones = detail.milestones;
        pack.milestones = milestones;
      } catch (e) {
        root.innerHTML = `<div class="splash"><div class="error">Failed to load pack: ${e.message}</div></div>`;
        setTimeout(() => ctx.goto('menu'), 2000);
        return;
      }
    }

    // Native UI: hide back/main during gameplay (canvas owns the screen).
    backButton.hide();
    mainButton.hide();

    // 2. Build DOM — streamlined HUD: lives | score | combo strip + meter
    root.innerHTML = `
      <div class="game-scene" id="stage">
        <div class="bg-girl" id="bgGirl"></div>
        <div class="bg-tint"></div>
        <div class="bg-scanlines"></div>
        <div class="bg-vignette"></div>
        <div class="light-left"></div>
        <div class="light-right"></div>
        <div class="fever-overlay" id="feverOverlay"></div>
        <canvas id="cv"></canvas>
        <div class="hud-top">
          <div class="hud-strip glass">
            <div class="hud-lives">
              <span class="heart" id="life0"></span>
              <span class="heart" id="life1"></span>
              <span class="heart" id="life2"></span>
            </div>
            <div class="hud-score-block">
              <div class="hud-score-value neon-white" id="hud-score">0</div>
            </div>
            <div class="hud-combo-block">
              <span class="hud-combo-x">×</span><span class="neon-pink" id="hud-combo">0</span>
            </div>
            <button class="hud-pause" id="pauseBtn" aria-label="Pause">⏸</button>
          </div>
          <div class="hud-meter glass">
            <div class="hud-meter-header">
              <span class="hud-stage" id="hud-stage">STAGE 1 / ${milestones.length} · ${(tuning.stage_names && tuning.stage_names[0]) || 'Stage'}</span>
              <span class="hud-pct" id="hud-pct">0%</span>
            </div>
            <div class="meter-bar"><div class="meter-fill" id="hud-meter"></div></div>
          </div>
        </div>
        <button class="end-btn" id="end-btn" style="display:none">FINISH</button>
        ${new URLSearchParams(location.search).get('fps') ? '<div id="fps-hud" class="fps-hud">FPS ··</div>' : ''}
      </div>`;

    const stage = root.querySelector('#stage');
    const canvas = root.querySelector('#cv');
    const cctx = canvas.getContext('2d');
    const bgGirl = root.querySelector('#bgGirl');
    const feverEl = root.querySelector('#feverOverlay');

    const hudScore = root.querySelector('#hud-score');
    const hudCombo = root.querySelector('#hud-combo');
    const lifeEls = [0, 1, 2].map((i) => root.querySelector('#life' + i));
    const hudStage = root.querySelector('#hud-stage');
    const hudPct = root.querySelector('#hud-pct');
    const hudMeter = root.querySelector('#hud-meter');
    const pauseBtn = root.querySelector('#pauseBtn');
    const endBtn = root.querySelector('#end-btn');

    // 3. Canvas setup
    const canvasInfo = setupCanvas(canvas);
    const onResize = () => canvasInfo.resize();
    window.addEventListener('resize', onResize);

    // 4. Background helpers — image as base + optional video overlay.
    // Singleton <video> element lives next to .bg-girl; we swap its src on
    // milestone unlock. Image loads first (fallback for slow video), video
    // fades in on canplay. When new slot has no video_url, the video hides.
    const bgVideo = document.createElement('video');
    bgVideo.autoplay = true;
    bgVideo.loop = true;
    bgVideo.muted = true;
    bgVideo.setAttribute('playsinline', '');
    bgVideo.setAttribute('webkit-playsinline', '');
    bgVideo.className = 'bg-video';
    bgVideo.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:0;transition:opacity .4s;pointer-events:none';
    bgVideo.addEventListener('canplay', () => {
      // Only show if this is still the active src (avoid races during rapid swaps).
      if (bgVideo.dataset.target === bgVideo.currentSrc || bgVideo.dataset.target === bgVideo.src) {
        bgVideo.style.opacity = '1';
      }
    });
    bgGirl.parentNode.insertBefore(bgVideo, bgGirl.nextSibling);

    const showBg = (imageURL, videoURL) => {
      if (imageURL && bgGirl.dataset.src !== imageURL) {
        const pre = new Image();
        pre.onload = () => {
          bgGirl.style.backgroundImage = `url('${imageURL}')`;
          bgGirl.classList.add('show');
          bgGirl.dataset.src = imageURL;
        };
        pre.src = imageURL;
      }
      if (videoURL) {
        if (bgVideo.dataset.src !== videoURL) {
          bgVideo.style.opacity = '0';
          bgVideo.dataset.src = videoURL;
          bgVideo.dataset.target = videoURL;
          bgVideo.src = videoURL;
          bgVideo.load();
        }
      } else {
        // No video for this slot — fade out + clear src so memory frees up.
        if (bgVideo.dataset.src) {
          bgVideo.style.opacity = '0';
          bgVideo.dataset.src = '';
          bgVideo.dataset.target = '';
          bgVideo.removeAttribute('src');
          bgVideo.load();
        }
      }
    };

    // 5. Choose initial bg: last unlocked slot (resume) OR cover (fresh).
    const lastUnlock = session.unlocked_slots?.length
      ? session.unlocked_slots[session.unlocked_slots.length - 1]
      : null;
    showBg(
      lastUnlock?.image_url || pack.cover_image_url || pack.cover_url,
      lastUnlock?.video_url || pack.cover_video_url || ''
    );

    // 6. Local session state (mutate in place as we POST /unlock /die /respawn)
    const sess = {
      id: session.id,
      unlocked_count: session.unlocked_count || 0,
      unlocked_slots: [...(session.unlocked_slots || [])],
      total_slots: pack.total_slots,
      status: session.status
    };

    // 7. Engine + restore unlocks
    const engine = new GameEngine({
      milestones,
      hooks: {
        onCatch: ({ kind, points, x, y, combo }) => {
          if (points > 0) {
            const screen = canvasInfo.logicalToScreen(x, y);
            floatScore(stage, {
              x: screen.x, y: screen.y, points,
              color: kind === 'golden' ? 'gold' : 'white'
            });
            particleBurst(stage, screen.x, screen.y, kind === 'golden' ? '#fde47a' : '#ff2bb1');
            haptic(kind === 'golden' ? 'medium' : 'light');
          }
          if (combo > 0 && combo % 5 === 0) {
            comboPopup(stage, combo);
            haptic('rigid');
          }
          updateHud();
        },
        onMiss: () => {
          shake(stage);
          haptic('warning');
          updateHud();
        },
        onMilestone: async ({ milestone, stageName }) => {
          stagePopup(stage, stageName);
          feverBanner(stage);
          haptic('success');
          feverEl.classList.add('on');
          setTimeout(() => feverEl.classList.remove('on'), 4500);
          if (milestone === 0) return;

          // POST /unlock — idempotent on BE side
          try {
            const resp = await api.unlockMilestone(sess.id, milestone);
            sess.unlocked_count = resp.unlocked_count;
            sess.unlocked_slots.push(resp.slot);
            showBg(resp.slot.image_url, resp.slot.video_url);
            // Show End button when all milestones unlocked.
            if (sess.unlocked_count >= sess.total_slots) {
              endBtn.style.display = 'block';
            }
          } catch (err) {
            console.warn('unlock failed', err);
          }
        },
        onGameOver: async () => {
          stopLoop();
          haptic('heavy');
          await handleDeath();
        }
      }
    });

    // Restore already-unlocked milestones (for resume)
    const unlockedThresholds = sess.unlocked_slots.map((s) => s.milestone).sort((a, b) => a - b);
    if (unlockedThresholds.length > 0) {
      engine.restoreUnlocks(unlockedThresholds);
      // Show End button immediately if user already unlocked all
      if (sess.unlocked_count >= sess.total_slots) {
        endBtn.style.display = 'block';
      }
    }

    // 8. Input
    const unbindInput = bindInput(canvas, engine.world, (x) => engine.moveBartender(x), canvasInfo);

    // 9. HUD updater
    const maxMilestone = milestones[milestones.length - 1] || 50;
    const updateHud = () => {
      hudScore.textContent = engine.score.toLocaleString();
      hudCombo.textContent = engine.combo;
      lifeEls.forEach((el, i) => el.classList.toggle('lost', i >= engine.lives));
      const curMs = milestones[engine.stageIdx] ?? 0;
      const nextMs = milestones[engine.stageIdx + 1] ?? maxMilestone;
      const span = Math.max(1, nextMs - curMs);
      const pct = Math.min(100, Math.max(0, ((engine.partyMeter - curMs) / span) * 100));
      hudMeter.style.width = pct + '%';
      hudPct.textContent = Math.floor(pct) + '%';
      hudStage.textContent = `STAGE ${engine.stageIdx + 1} / ${milestones.length} · ${engine.stageName()}`;
    };
    updateHud();

    // 10. Game loop
    let rafId = 0;
    let last = performance.now();
    let paused = false;
    // FPS HUD — chỉ ON khi URL có ?fps=1. Tracker dùng rolling window 1s + minFPS
    // since-mount để spot drop frames.
    const fpsEl = root.querySelector('#fps-hud');
    let fpsFrames = 0, fpsWindowStart = last, fpsMin = Infinity;
    const loop = (now) => {
      if (!engine.running) return;
      if (!paused) {
        const dt = Math.min(0.1, (now - last) / 1000);
        engine.update(dt, now);
        render(cctx, engine, canvasInfo);
        if (fpsEl) {
          fpsFrames++;
          if (now - fpsWindowStart >= 1000) {
            const fps = Math.round((fpsFrames * 1000) / (now - fpsWindowStart));
            if (fps < fpsMin) fpsMin = fps;
            fpsEl.textContent = `FPS ${fps} · min ${fpsMin}`;
            fpsFrames = 0;
            fpsWindowStart = now;
          }
        }
      }
      last = now;
      rafId = requestAnimationFrame(loop);
    };
    const stopLoop = () => {
      engine.stop();
      cancelAnimationFrame(rafId);
    };
    const startLoop = () => {
      engine.start();
      last = performance.now();
      rafId = requestAnimationFrame(loop);
    };

    // 11. Death + respawn flow
    const handleDeath = async () => {
      let dieResp;
      try {
        dieResp = await api.sessionDie(sess.id);
      } catch (e) {
        console.warn('die failed', e);
        // Fallback — go to result
        ctx.state.result = { score: engine.score, bestCombo: engine.bestCombo, mugsCaught: engine.partyMeter, pack: pack.name };
        return ctx.goto('result');
      }
      sess.status = 'pending_respawn';
      openRespawnModal(dieResp);
    };

    // Try respawn (POST /respawn). On success → reset + resume.
    // On 402 → show insufficient-star modal with Buy stars option.
    // On other errors → bail to result.
    const tryRespawn = async (lastCost, lastBalance) => {
      try {
        const resp = await api.sessionRespawn(sess.id);
        if (ctx.state.user) ctx.state.user.star_balance = resp.balance;
        sess.status = 'active';
        engine.reset();
        const ths = sess.unlocked_slots.map((s) => s.milestone).sort((a, b) => a - b);
        engine.restoreUnlocks(ths);
        updateHud();
        startLoop();
      } catch (e) {
        if (e.status === 402) {
          const required = e.details?.required ?? lastCost;
          const current = e.details?.current ?? lastBalance;
          showInsufficientWithBuy(required, current);
        } else {
          alert(`Respawn failed: ${e.message}`);
          endAndExit();
        }
      }
    };

    // Insufficient → offer Buy stars (suggest = shortfall). After purchase,
    // refresh balance from BE then auto-retry respawn. On user cancel → re-show
    // insufficient modal so user can quit or try again.
    const showInsufficientWithBuy = (required, current) => {
      showRespawnModal(stage, {
        variant: 'insufficient-star',
        required,
        balance: current,
        unlocked: sess.unlocked_count,
        total: sess.total_slots,
        onBuyStars: async () => {
          const shortfall = Math.max(1, required - current);
          const result = await openTopupSheet(stage, { balance: current, suggested: shortfall });
          if (result?.status === 'paid' || result?.status === 'pending') {
            // Refresh balance authoritative from BE (webhook credited the ledger).
            try {
              const me = await api.getMe();
              ctx.state.user = me;
            } catch {}
            await tryRespawn(required, ctx.state.user?.star_balance ?? current);
          } else {
            // Cancelled/failed/closed — re-show insufficient with latest known balance.
            showInsufficientWithBuy(required, ctx.state.user?.star_balance ?? current);
          }
        },
        onQuit: () => endAndExit()
      });
    };

    const openRespawnModal = (dieResp) => {
      const variant = dieResp.can_respawn ? 'respawn-available' : 'cannot-respawn';
      showRespawnModal(stage, {
        variant,
        cost: dieResp.respawn_cost,
        balance: dieResp.balance,
        unlocked: sess.unlocked_count,
        total: sess.total_slots,
        onRespawn: () => tryRespawn(dieResp.respawn_cost, dieResp.balance),
        onQuit: () => endAndExit()
      });
    };

    const endAndExit = async () => {
      let endResp = null;
      try {
        endResp = await api.sessionEnd(sess.id, engine.score);
      } catch (e) {
        console.warn('end failed', e);
      }
      // Last unlocked image for result BG (highest milestone). Falls back to cover.
      const lastSlot = sess.unlocked_slots.length
        ? sess.unlocked_slots[sess.unlocked_slots.length - 1]
        : null;
      ctx.state.result = {
        score: engine.score,
        bestCombo: engine.bestCombo,
        mugsCaught: engine.partyMeter,
        stageIdx: engine.stageIdx,
        pack: pack.name,
        finalImage: lastSlot?.image_url || pack.cover_image_url || pack.cover_url,
        highScore: endResp?.high_score ?? 0,
        newHighScore: !!endResp?.new_high_score
      };
      // Clear session-related state
      ctx.state.activeSession = null;
      ctx.state.startWithRespawnModal = false;
      ctx.goto('result');
    };

    // 12. End button (only when all unlocked) → /end → result
    endBtn.onclick = () => endAndExit();

    // 13. Pause + visibility — bottom sheet pattern (same chrome as respawn)
    let pauseOverlay = null;
    const showPause = () => {
      paused = true;
      pauseBtn.textContent = '▶';
      haptic('light');

      const total = sess.total_slots;
      const unlocked = sess.unlocked_count;
      const dotsHTML = Array.from({ length: total }, (_, i) =>
        `<span class="rs-dot ${i < unlocked ? 'on' : ''}"></span>`
      ).join('');

      pauseOverlay = document.createElement('div');
      pauseOverlay.className = 'modal-overlay pause-overlay';
      pauseOverlay.innerHTML = `
        <div class="pause-sheet">
          <div class="sheet-handle"></div>
          <div class="rs-hero">
            <div class="pause-icon">⏸</div>
            <div class="rs-title rs-title-pink">PAUSED</div>
            <div class="rs-sub">Catch your breath. The mugs aren't going anywhere.</div>
          </div>
          <div class="rs-progress">
            <div class="rs-progress-label">PROGRESS · ${unlocked}/${total} STAGES</div>
            <div class="rs-dots">${dotsHTML}</div>
          </div>
          <button class="btn-primary rs-cta" id="btn-resume">
            <span class="rs-cta-label">RESUME GAME</span>
          </button>
          <button class="btn-ghost pause-exit" id="btn-exit-pause">END GAME</button>
        </div>`;
      stage.appendChild(pauseOverlay);
      const sheet = pauseOverlay.querySelector('.pause-sheet');
      requestAnimationFrame(() => sheet.classList.add('show'));

      pauseOverlay.querySelector('#btn-resume').onclick = () => {
        haptic('medium');
        hidePause();
      };
      pauseOverlay.querySelector('#btn-exit-pause').onclick = () => {
        haptic('warning');
        // Close pause sheet synchronously (no animation) since we're transitioning scenes.
        pauseOverlay?.remove();
        pauseOverlay = null;
        paused = false;
        endAndExit();
      };
    };
    const hidePause = () => {
      if (!pauseOverlay) {
        paused = false;
        pauseBtn.textContent = '⏸';
        last = performance.now();
        return;
      }
      const sheet = pauseOverlay.querySelector('.pause-sheet');
      const overlay = pauseOverlay;
      pauseOverlay = null;
      sheet?.classList.add('closing');
      setTimeout(() => overlay.remove(), 220);
      paused = false;
      pauseBtn.textContent = '⏸';
      last = performance.now();
    };
    pauseBtn.onclick = () => (paused ? hidePause() : showPause());
    const onVis = () => {
      if (document.hidden && engine.running && !paused) showPause();
    };
    document.addEventListener('visibilitychange', onVis);

    this._cleanup = () => {
      unbindInput();
      stopLoop();
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
      bgVideo.pause();
      bgVideo.removeAttribute('src');
      bgVideo.load();
      bgVideo.remove();
    };

    // 14. Initial state — branch based on startWithRespawnModal flag
    if (ctx.state.startWithRespawnModal) {
      // Resume from pending_respawn — show modal immediately with cost from session DTO
      ctx.state.startWithRespawnModal = false;
      openRespawnModal({
        respawn_cost: session.respawn_cost,
        can_respawn: session.can_respawn,
        balance: session.balance
      });
    } else {
      startLoop();
    }
  },

  async destroy() {
    this._cleanup?.();
  }
};
